import {
  lazy,
  Suspense,
  type ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { initAnalytics } from "./helpers/analytics.helper";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Box, CircularProgress, CssBaseline } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useAuthStore } from "./stores/auth.store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Layout from "./components/Layout";
import PermissionDeniedOverlay from "./components/PermissionDeniedOverlay";
import AuthRoute from "./components/AuthRoute";
import HomeRedirect from "./components/HomeRedirect";
import {
  PERM_APPROVAL_LIST,
  PERM_FLOW_STEP_TYPE_LIST,
  PERM_VERIFICATION_BATCH_LIST,
  PERM_VERIFICATION_BATCH_READ,
  PERM_VERIFICATION_CALCULATION_RUN_READ,
  PERM_ESG_EMISSION_FACTOR_LIST,
  PERM_ESG_UTILITY_FACTOR_LIST,
  PERM_ESG_MRV_LIST,
  PERM_ESG_MRV_READ,
  PERM_WASTE_CATALOG_LIST,
  PERM_ORG_CREATE,
  PERM_ADMIN_PRE_ORDER_LIST,
  PERM_ADMIN_USER_LIST,
  PERM_PRE_ORDER_LIST,
  PERM_USER_LIST,
  PERM_API_KEY_LIST,
  PERM_NOTIFICATION_SEND,
  PERM_ADMIN_PICKUP_CREATE_HISTORICAL,
  PERM_DEVICE_LIST,
  PERM_DEVICE_READ,
  PERM_DEVICE_TYPE_LIST,
  USER_MANAGEMENT_ROLES,
} from "./auth";
import HelpI18nProvider from "./components/help/HelpI18nProvider";
import "./i18n/help";

const RouteLoadingFallback = () => (
  <Box
    role="status"
    aria-label="Loading page"
    sx={{ minHeight: 240, display: "flex", alignItems: "center", justifyContent: "center" }}
  >
    <CircularProgress size={36} />
  </Box>
);

function lazyPage(load: () => Promise<{ default: ComponentType }>) {
  const Page = lazy(load);
  return function LazyRoutePage() {
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <Page />
      </Suspense>
    );
  };
}

const Login = lazyPage(() => import("./pages/Login"));
const Dashboard = lazyPage(() => import("./pages/Dashboard"));
const Organizations = lazyPage(() => import("./pages/Organizations"));
const OrganizationForm = lazyPage(() => import("./pages/organizations/OrganizationForm"));
const Users = lazyPage(() => import("./pages/Users"));
const UserEdit = lazyPage(() => import("./pages/UserEdit"));
const ApprovalsInbox = lazyPage(() => import("./pages/approvals/ApprovalsInbox"));
const VerificationBatches = lazyPage(() => import("./pages/verification/VerificationBatches"));
const VerificationBatchDetail = lazyPage(() => import("./pages/verification/VerificationBatchDetail"));
const VerificationCalculationRunDetail = lazyPage(
  () => import("./pages/verification/VerificationCalculationRunDetail"),
);
const ApiKeysPage = lazyPage(() => import("./pages/api_keys/ApiKeysPage"));
const SendNotificationPage = lazyPage(() => import("./pages/notifications/SendNotificationPage"));
const ApiKeyActivityPage = lazyPage(() => import("./pages/api_keys/ApiKeyActivityPage"));
const SupplierOutletLinksPage = lazyPage(() => import("./pages/suppliers/SupplierOutletLinksPage"));
const SupplierOutletDetailPage = lazyPage(() => import("./pages/suppliers/SupplierOutletDetailPage"));
const FacilityLinksPage = lazyPage(() => import("./pages/facilities/FacilityLinksPage"));

// Devices
const DevicesPage = lazyPage(() => import("./pages/devices/DevicesPage"));
const DeviceTypesPage = lazyPage(() => import("./pages/devices/DeviceTypesPage"));
const DeviceDetailPage = lazyPage(() => import("./pages/devices/DeviceDetailPage"));

// ESG
const EmissionFactorLibrary = lazyPage(() => import("./pages/esg/EmissionFactorLibrary"));
const EmissionFactorDetail = lazyPage(() => import("./pages/esg/EmissionFactorDetail"));
const UtilityEmissionFactors = lazyPage(() => import("./pages/esg/UtilityEmissionFactors"));
const MrvMethodologyLibrary = lazyPage(() => import("./pages/esg/MrvMethodologyLibrary"));
const MrvMethodologyDetail = lazyPage(() => import("./pages/esg/MrvMethodologyDetail"));
const MrvMethodologySheetView = lazyPage(
  () => import("./pages/esg/MrvMethodologySheetView"),
);

// Orders
const PickupOrdersActive = lazyPage(() => import("./pages/pickup_orders/PickupOrdersActive"));
const PickupOrdersCreate = lazyPage(() => import("./pages/pickup_orders/PickupOrdersCreate"));
const PickupOrdersRegularCreate = lazyPage(
  () => import("./pages/pickup_orders/PickupOrdersRegularCreate"),
);
const PickupOrdersHistoricalCreate = lazyPage(
  () => import("./pages/pickup_orders/PickupOrdersHistoricalCreate"),
);
const PickupOrderCompletePage = lazyPage(
  () => import("./pages/pickup_orders/PickupOrderCompletePage"),
);
const BatchCompleteSelectPage = lazyPage(
  () => import("./pages/pickup_orders/BatchCompleteSelectPage"),
);

// Transportation
const FleetManagement = lazyPage(() => import("./pages/transportation/FleetManagement"));
const TransportationRoutes = lazyPage(
  () => import("./pages/transportation/TransportationRoutes"),
);
const Drivers = lazyPage(() => import("./pages/transportation/Drivers"));
const DriverDetail = lazyPage(() => import("./pages/transportation/DriverDetail"));
const TruckTracking = lazyPage(() => import("./pages/transportation/TruckTracking"));
const TruckTrackingDetail = lazyPage(
  () => import("./pages/transportation/TruckTrackingDetail"),
);
const Deliveries = lazyPage(() => import("./pages/Deliveries"));
const DeliveryDetail = lazyPage(() => import("./pages/DeliveryDetail"));
const BatchCompletePage = lazyPage(() => import("./pages/deliveries/BatchCompletePage"));
const AggregationBatchesPage = lazyPage(
  () => import("./pages/deliveries/AggregationBatchesPage"),
);

// Processing
const ProcessingCenters = lazyPage(() => import("./pages/processing/ProcessingCenters"));
const ProcessingQueue = lazyPage(() => import("./pages/processing/ProcessingQueue"));
const QualityControl = lazyPage(() => import("./pages/processing/QualityControl"));

// Recycling
const SortingLines = lazyPage(() => import("./pages/recycling/SortingLines"));
const MaterialTypes = lazyPage(() => import("./pages/recycling/MaterialTypes"));
const WasteCatalogHub = lazyPage(() => import("./pages/waste_catalog/WasteCatalogHub"));
const FlowStepCatalogue = lazyPage(() => import("./pages/operations/FlowStepCatalogue"));
const RecyclingQuality = lazyPage(() => import("./pages/recycling/Quality"));

// Containers
const ContainerList = lazyPage(() => import("./pages/containers/ContainerList"));
const CollectionSites = lazyPage(() => import("./pages/containers/CollectionSites"));

// Market and invoices
const Orders = lazyPage(() => import("./pages/Orders"));
const PreOrders = lazyPage(() => import("./pages/PreOrders"));
const InvoicesPage = lazyPage(() => import("./pages/invoices/Invoices"));

// Operations (internal admin)
const WasteMoversOpsList = lazyPage(() => import("./pages/operations/WasteMoversOpsList"));
const WasteMoverOpsDetail = lazyPage(() => import("./pages/operations/WasteMoverOpsDetail"));
const WasteOutletsOpsList = lazyPage(() => import("./pages/operations/WasteOutletsOpsList"));
const WasteOutletOpsDetail = lazyPage(() => import("./pages/operations/WasteOutletOpsDetail"));
const RecyclersOpsList = lazyPage(() => import("./pages/operations/RecyclersOpsList"));
const FinancePage = lazyPage(() => import("./pages/finance/FinancePage"));
const RecyclerFinanceDetail = lazyPage(() => import("./pages/finance/RecyclerFinanceDetail"));
const WasteOutletFinanceDetail = lazyPage(() => import("./pages/finance/WasteOutletFinanceDetail"));
const RecyclerOpsDetail = lazyPage(() => import("./pages/operations/RecyclerOpsDetail"));
const KnowledgeBase = lazyPage(() => import("./pages/support/KnowledgeBase"));
const KnowledgeBaseArticle = lazyPage(() => import("./pages/support/KnowledgeBaseArticle"));

const queryClient = new QueryClient();

const baseTheme = {
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          padding: "8px 16px",
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderBottom: "1px solid",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "1px solid",
        },
      },
    },
  },
};

function App() {
  const { isAuthenticated, initAuth } = useAuthStore();
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const savedMode = localStorage.getItem("themeMode");
    return (
      (savedMode as "light" | "dark") ||
      (window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light")
    );
  });

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", newMode);
      return newMode;
    });
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        ...baseTheme,
        palette: {
          mode,
          primary: {
            main: "#DC2626",
            light: "#EF4444",
            dark: "#B91C1C",
            contrastText: "#FFFFFF",
          },
          secondary: {
            main: "#0EA5E9",
            light: "#38BDF8",
            dark: "#0284C7",
            contrastText: "#FFFFFF",
          },
          background: {
            default: mode === "dark" ? "#0F172A" : "#F8FAFC",
            paper: mode === "dark" ? "#1E293B" : "#FFFFFF",
          },
          text: {
            primary: mode === "dark" ? "#F8FAFC" : "#1E293B",
            secondary: mode === "dark" ? "#94A3B8" : "#64748B",
          },
          divider:
            mode === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.06)",
        },
        components: {
          ...baseTheme.components,
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                color: mode === "dark" ? "#F8FAFC" : "#1E293B",
                borderColor:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(0, 0, 0, 0.06)",
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                borderColor:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(0, 0, 0, 0.06)",
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                boxShadow:
                  mode === "dark"
                    ? "0 4px 20px rgba(0, 0, 0, 0.2)"
                    : "0 4px 20px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow:
                    mode === "dark"
                      ? "0 8px 30px rgba(0, 0, 0, 0.3)"
                      : "0 8px 30px rgba(0, 0, 0, 0.1)",
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                transition: "all 0.3s ease-in-out",
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                padding: "8px 16px",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                },
              }, // ff0040
              contained: {
                background: `#ff0040`,
                "&:hover": {
                  background: `linear-gradient(90deg, #0EA5E9 0%, #DC2626 100%)`,
                  boxShadow: "0 4px 20px rgba(220, 38, 38, 0.4)",
                },
              },
            },
          },
        },
      }),
    [mode]
  );

  useEffect(() => {
    initAnalytics();
    initAuth();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <PermissionDeniedOverlay />
        <Router>
          <Routes>
            <Route
              path="/login"
              element={!isAuthenticated ? <Login /> : <Navigate to="/" />}
            />
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <HomeRedirect>
                      <Dashboard />
                    </HomeRedirect>
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/organizations"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Organizations />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/organizations/new"
              element={
                <AuthRoute requiredPermission={PERM_ORG_CREATE}>
                  <Layout onThemeToggle={toggleTheme}>
                    <OrganizationForm />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/organizations/:organizationId/edit"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <OrganizationForm />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/users"
              element={
                <AuthRoute
                  requiredPermission={PERM_ADMIN_USER_LIST}
                  orgScopedPermission={PERM_USER_LIST}
                  legacyRoles={USER_MANAGEMENT_ROLES}
                >
                  <Layout onThemeToggle={toggleTheme}>
                    <Users />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/api-keys"
              element={
                <AuthRoute requiredPermission={PERM_API_KEY_LIST}>
                  <Layout onThemeToggle={toggleTheme}>
                    <ApiKeysPage />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/api-keys/:apiKeyId/activity"
              element={
                <AuthRoute requiredPermission={PERM_API_KEY_LIST}>
                  <Layout onThemeToggle={toggleTheme}>
                    <ApiKeyActivityPage />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/notifications/send"
              element={
                <AuthRoute requiredPermission={PERM_NOTIFICATION_SEND}>
                  <Layout onThemeToggle={toggleTheme}>
                    <SendNotificationPage />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/approvals"
              element={
                <AuthRoute requiredPermission={PERM_APPROVAL_LIST}>
                  <Layout onThemeToggle={toggleTheme}>
                    <ApprovalsInbox />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/verification/batches"
              element={
                <AuthRoute requiredPermission={PERM_VERIFICATION_BATCH_LIST}>
                  <Layout onThemeToggle={toggleTheme}>
                    <VerificationBatches />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/verification/batches/:batchId"
              element={
                <AuthRoute requiredPermission={PERM_VERIFICATION_BATCH_READ}>
                  <Layout onThemeToggle={toggleTheme}>
                    <VerificationBatchDetail />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/verification/calculation-runs/:runId"
              element={
                <AuthRoute requiredPermission={PERM_VERIFICATION_CALCULATION_RUN_READ}>
                  <Layout onThemeToggle={toggleTheme}>
                    <VerificationCalculationRunDetail />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/finance"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <FinancePage />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/finance/recyclers/:organizationId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <RecyclerFinanceDetail />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/finance/waste-outlets/:organizationId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <WasteOutletFinanceDetail />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/users/new"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <UserEdit />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/users/:userId/edit"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <UserEdit />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/esg/mrv-methodologies"
              element={
                <AuthRoute requiredPermission={PERM_ESG_MRV_LIST}>
                  <Layout onThemeToggle={toggleTheme}>
                    <MrvMethodologyLibrary />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/esg/mrv-methodologies/:id"
              element={
                <AuthRoute requiredPermission={PERM_ESG_MRV_READ}>
                  <Layout onThemeToggle={toggleTheme}>
                    <MrvMethodologyDetail />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/esg/mrv-methodologies/:id/sheet-view"
              element={
                <AuthRoute requiredPermission={PERM_ESG_MRV_READ}>
                  <Layout onThemeToggle={toggleTheme}>
                    <MrvMethodologySheetView />
                  </Layout>
                </AuthRoute>
              }
            />

            <Route
              path="/esg/emission-factors"
              element={
                <AuthRoute requiredPermission={PERM_ESG_EMISSION_FACTOR_LIST}>
                  <Layout onThemeToggle={toggleTheme}>
                    <EmissionFactorLibrary />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/esg/emission-factors/:emissionFactorId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <EmissionFactorDetail />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/esg/utility-emission-factors"
              element={
                <AuthRoute requiredPermission={PERM_ESG_UTILITY_FACTOR_LIST}>
                  <Layout onThemeToggle={toggleTheme}>
                    <UtilityEmissionFactors />
                  </Layout>
                </AuthRoute>
              }
            />

            <Route
              path="/operations/waste-movers"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <WasteMoversOpsList />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/operations/waste-movers/:organizationId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <WasteMoverOpsDetail />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/operations/waste-outlets"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <WasteOutletsOpsList />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/operations/supplier-links"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <SupplierOutletLinksPage />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/operations/supplier-links/:outletOrgId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <SupplierOutletDetailPage />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/operations/facility-links"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <FacilityLinksPage />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/operations/waste-outlets/:organizationId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <WasteOutletOpsDetail />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/operations/recyclers"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <RecyclersOpsList />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/operations/recyclers/:organizationId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <RecyclerOpsDetail />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Orders Routes */}
            <Route
              path="/pickup_orders"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <PickupOrdersActive />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/pickup_orders/regular/create"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <PickupOrdersRegularCreate />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/pickup_orders/create"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <PickupOrdersCreate />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/pickup_orders/batch-complete"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <BatchCompleteSelectPage />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/pickup_orders/:orderId/edit"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <PickupOrdersCreate />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/pickup_orders/:orderId/complete"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <PickupOrderCompletePage />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/pickup_orders/historical/create"
              element={
                <AuthRoute requiredPermission={PERM_ADMIN_PICKUP_CREATE_HISTORICAL}>
                  <Layout onThemeToggle={toggleTheme}>
                    <PickupOrdersHistoricalCreate />
                  </Layout>
                </AuthRoute>
              }
            />

            {/* Transportation Routes */}
            <Route
              path="/transportation/fleet"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <FleetManagement />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            {/* <Route
              path="/transportation/drivers"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Drivers />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            /> */}
            <Route
              path="/transportation/routes"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <TransportationRoutes />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
          
            <Route
              path="/transportation/drivers"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Drivers />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/transportation/drivers/:driverId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <DriverDetail />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            
            <Route
              path="/deliveries"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Deliveries />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/deliveries/aggregations"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <AggregationBatchesPage />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/deliveries/aggregations/:aggregationKey/complete"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <BatchCompletePage />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/deliveries/:deliveryId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <DeliveryDetail />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/truck_tracking"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <TruckTracking />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/truck_tracking/:truckId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <TruckTrackingDetail />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Processing Routes */}
            <Route
              path="/processing/centers"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <ProcessingCenters />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/processing/queue"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <ProcessingQueue />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/processing/quality"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <QualityControl />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/operations/flow-steps"
              element={
                <AuthRoute requiredPermission={PERM_FLOW_STEP_TYPE_LIST}>
                  <Layout onThemeToggle={toggleTheme}>
                    <FlowStepCatalogue />
                  </Layout>
                </AuthRoute>
              }
            />

            {/* Recycling Routes */}
            <Route
              path="/recycling/waste-types"
              element={
                <AuthRoute requiredPermission={PERM_WASTE_CATALOG_LIST}>
                  <Layout onThemeToggle={toggleTheme}>
                    <WasteCatalogHub />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route path="/recycling/waste-catalog" element={<Navigate to="/recycling/waste-types" replace />} />
            <Route
              path="/recycling/sorting"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <SortingLines />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/recycling/materials"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <MaterialTypes />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/recycling/quality"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <RecyclingQuality />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Container Routes */}
            <Route
              path="/containers/list"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <ContainerList />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/containers/sites"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <CollectionSites />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/containers/sensors"
              element={<Navigate to="/devices" replace />}
            />

            <Route
              path="/devices"
              element={
                <AuthRoute requiredPermission={PERM_DEVICE_LIST}>
                  <Layout onThemeToggle={toggleTheme}>
                    <DevicesPage />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/devices/types"
              element={
                <AuthRoute requiredPermission={PERM_DEVICE_TYPE_LIST}>
                  <Layout onThemeToggle={toggleTheme}>
                    <DeviceTypesPage />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/devices/:id"
              element={
                <AuthRoute requiredPermission={PERM_DEVICE_READ}>
                  <Layout onThemeToggle={toggleTheme}>
                    <DeviceDetailPage />
                  </Layout>
                </AuthRoute>
              }
            />

            <Route
              path="/orders"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Orders />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/pre_orders"
              element={
                <AuthRoute
                  requiredPermission={PERM_ADMIN_PRE_ORDER_LIST}
                  orgScopedPermission={PERM_PRE_ORDER_LIST}
                >
                  <Layout onThemeToggle={toggleTheme}>
                    <PreOrders />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <InvoicesPage />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route path="/offers" element={<Navigate to="/" replace />} />
            <Route
              path="/pre-orders"
              element={
                <AuthRoute
                  requiredPermission={PERM_ADMIN_PRE_ORDER_LIST}
                  orgScopedPermission={PERM_PRE_ORDER_LIST}
                >
                  <Layout onThemeToggle={toggleTheme}>
                    <PreOrders />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/knowledge-base"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <HelpI18nProvider>
                      <KnowledgeBase />
                    </HelpI18nProvider>
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/knowledge-base/:articleId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <HelpI18nProvider>
                      <KnowledgeBaseArticle />
                    </HelpI18nProvider>
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </Router>
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
